<script setup>
import { ref, computed, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Button from '../../../../components-next/button/Button.vue';
import BulkStageFilter from './BulkStageFilter.vue';

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['close', 'confirm']);
const { t } = useI18n();
const store = useStore();

const selectedItems = ref([]);
const selectedStage = ref('');
const avatarErrors = ref({});

// Fase 4.3 — origem filter (default 'all'). Narrow apenas a lista visível
// e a seleção (não envia ao backend — destino vem em selectedStage).
const stageFilter = ref('all');

// Custom dropdown state
const showStageDropdown = ref(false);
const stageButtonRef = ref(null);
const stageDropdownStyle = ref({});

const stages = computed(() => {
  const pipeline = store.getters['pipelines/getSelectedPipeline'];
  if (!pipeline?.stages) return [];

  return Object.entries(pipeline.stages)
    .map(([id, stage]) => ({
      id,
      name: stage.name,
      position: stage.position,
    }))
    .sort((a, b) => a.position - b.position);
});

const selectedStageName = computed(() => {
  const found = stages.value.find(s => s.id === selectedStage.value);
  return found ? found.name : null;
});

const toggleStageDropdown = () => {
  showStageDropdown.value = !showStageDropdown.value;
  if (showStageDropdown.value) {
    nextTick(() => {
      if (stageButtonRef.value) {
        const rect = stageButtonRef.value.getBoundingClientRect();
        stageDropdownStyle.value = {
          top: `${rect.bottom + 4}px`,
          left: `${rect.left}px`,
          width: `${rect.width}px`,
        };
      }
    });
  }
};

const selectStage = stageId => {
  selectedStage.value = stageId;
  showStageDropdown.value = false;
};

const getAvatarUrl = item => {
  const details = item.item_details;
  if (details?.contact_thumbnail?.trim()) return details.contact_thumbnail;
  const conv = details?.conversation?.contact;
  if (conv?.thumbnail?.trim()) return conv.thumbnail;
  if (conv?.avatar_url?.trim()) return conv.avatar_url;
  if (item.contact?.thumbnail?.trim()) return item.contact.thumbnail;
  if (item.contact?.avatar_url?.trim()) return item.contact.avatar_url;
  return null;
};

const getContactName = item => {
  const conv = item.item_details?.conversation?.contact;
  if (conv?.name) return conv.name;
  if (item.contact?.name) return item.contact.name;
  return item.item_details?.title || item.title || '?';
};

const getInitials = item => {
  const name = getContactName(item);
  return name.charAt(0).toUpperCase();
};

const handleAvatarError = itemId => {
  avatarErrors.value = { ...avatarErrors.value, [itemId]: true };
};

const priorityConfig = priority => {
  const map = {
    urgent: { bg: 'bg-n-ruby-3', text: 'text-n-ruby-11' },
    high: { bg: 'bg-orange-3', text: 'text-orange-11' },
    medium: { bg: 'bg-n-amber-3', text: 'text-n-amber-11' },
    low: { bg: 'bg-blue-3', text: 'text-blue-11' },
  };
  return map[priority] || { bg: 'bg-n-alpha-2', text: 'text-n-slate-10' };
};

// Items realmente exibidos — narrow pelo stage filter (Fase 4.3).
// Quando 'all', mostra a lista inteira; caso contrário, apenas a etapa
// escolhida no filtro. SelectAll opera apenas sobre os visíveis.
const filteredItems = computed(() => {
  if (stageFilter.value === 'all') return props.items;
  return props.items.filter(
    item => (item.pipeline_stage || item.stage) === stageFilter.value
  );
});

const toggleItem = itemId => {
  const index = selectedItems.value.indexOf(itemId);
  if (index === -1) {
    selectedItems.value.push(itemId);
  } else {
    selectedItems.value.splice(index, 1);
  }
};

const toggleSelectAll = () => {
  const visibleIds = filteredItems.value.map(item => item.id);
  const allVisibleSelected = visibleIds.every(id =>
    selectedItems.value.includes(id)
  );
  if (allVisibleSelected) {
    selectedItems.value = selectedItems.value.filter(
      id => !visibleIds.includes(id)
    );
  } else {
    selectedItems.value = Array.from(
      new Set([...selectedItems.value, ...visibleIds])
    );
  }
};

const isAllSelected = computed(() => {
  const visibleIds = filteredItems.value.map(item => item.id);
  return (
    visibleIds.length > 0 &&
    visibleIds.every(id => selectedItems.value.includes(id))
  );
});

const handleConfirm = () => {
  if (selectedStage.value && selectedItems.value.length > 0) {
    emit('confirm', {
      itemIds: selectedItems.value,
      stageId: selectedStage.value,
    });
  }
};
</script>

<template>
  <div
    class="w-full max-w-[600px]"
    @click="showStageDropdown && (showStageDropdown = false)"
  >
    <div class="p-6">
      <h3 class="text-heading-2 text-n-slate-12 mb-1">
        {{ t('PIPELINE.BULK_ACTIONS.MOVE.TITLE') }}
      </h3>
      <p class="text-body-main text-n-slate-11 mb-4">
        {{ t('PIPELINE.BULK_ACTIONS.MOVE.DESCRIPTION') }}
      </p>

      <!-- Stage Selector (custom dropdown) -->
      <div class="mb-4" @click.stop>
        <label class="block text-label text-n-slate-12 mb-2">
          {{ t('PIPELINE.BULK_ACTIONS.SELECT_STAGE') }}
        </label>
        <button
          ref="stageButtonRef"
          type="button"
          class="w-full h-10 flex items-center justify-between gap-2 px-3 rounded-lg border border-n-weak bg-n-solid-2 hover:bg-n-alpha-1 transition-colors text-sm"
          :class="
            showStageDropdown ? 'border-n-brand ring-2 ring-n-brand/20' : ''
          "
          @click="toggleStageDropdown"
        >
          <span
            :class="selectedStageName ? 'text-n-slate-12' : 'text-n-slate-9'"
          >
            {{
              selectedStageName ||
              t('PIPELINE.BULK_ACTIONS.BULK_FORM.STAGE.PLACEHOLDER')
            }}
          </span>
          <span
            class="i-lucide-chevron-down size-4 text-n-slate-9 flex-shrink-0 transition-transform duration-150"
            :class="showStageDropdown ? 'rotate-180' : ''"
          />
        </button>

        <!-- Teleported dropdown to avoid clipping -->
        <Teleport to="body">
          <Transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <div
              v-if="showStageDropdown"
              class="fixed z-dropdown-overlay py-1 rounded-xl shadow-lg bg-n-solid-2 border border-n-weak origin-top"
              :style="stageDropdownStyle"
              @click.stop
            >
              <button
                v-for="stage in stages"
                :key="stage.id"
                type="button"
                class="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-2 transition-colors text-left"
                :class="
                  selectedStage === stage.id ? 'bg-n-alpha-2 font-medium' : ''
                "
                @click="selectStage(stage.id)"
              >
                <span
                  v-if="selectedStage === stage.id"
                  class="i-lucide-check size-3.5 text-n-brand flex-shrink-0"
                />
                <span v-else class="size-3.5 flex-shrink-0" />
                {{ stage.name }}
              </button>
            </div>
          </Transition>
        </Teleport>
      </div>

      <!-- Fase 4.3 — origem filter: limita a lista visível por etapa -->
      <div v-if="stages.length > 1" class="mb-4" @click.stop>
        <BulkStageFilter
          v-model="stageFilter"
          :items="items"
          :stages="stages"
        />
      </div>

      <!-- Items List -->
      <div
        class="max-h-96 overflow-y-auto mb-4 border border-n-weak rounded-xl"
      >
        <!-- Select All -->
        <div
          class="flex items-center gap-3 px-3 py-2.5 cursor-pointer border-b border-n-weak hover:bg-n-alpha-1 transition-colors rounded-t-xl"
          @click="toggleSelectAll"
        >
          <input
            type="checkbox"
            :checked="isAllSelected"
            class="size-4 flex-shrink-0 accent-n-brand"
            @click.stop
          />
          <span class="text-label font-medium text-n-slate-12">
            {{ t('PIPELINE.BULK_ACTIONS.SELECT_ALL') }}
          </span>
          <span class="ml-auto text-label-small text-n-slate-10">
            {{
              selectedItems.filter(id => filteredItems.some(i => i.id === id))
                .length
            }}/{{ filteredItems.length }}
          </span>
        </div>

        <!-- Individual Items (narrowed by stageFilter) -->
        <div
          v-for="(item, index) in filteredItems"
          :key="item.id"
          class="flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors"
          :class="[
            selectedItems.includes(item.id)
              ? 'bg-n-blue-2 dark:bg-n-blue-3/20'
              : 'hover:bg-n-alpha-1',
            index < filteredItems.length - 1 ? 'border-b border-n-weak' : '',
            index === filteredItems.length - 1 ? 'rounded-b-xl' : '',
          ]"
          @click="toggleItem(item.id)"
        >
          <input
            type="checkbox"
            :checked="selectedItems.includes(item.id)"
            class="size-4 flex-shrink-0 accent-n-brand"
            @click.stop
          />

          <!-- Avatar -->
          <div class="flex-shrink-0">
            <img
              v-if="getAvatarUrl(item) && !avatarErrors[item.id]"
              :src="getAvatarUrl(item)"
              :alt="getContactName(item)"
              class="size-8 rounded-full object-cover ring-2 ring-n-solid-2"
              @error="handleAvatarError(item.id)"
            />
            <div
              v-else
              class="size-8 rounded-full bg-n-alpha-3 ring-2 ring-n-solid-2 flex items-center justify-center"
            >
              <span class="text-xs font-bold text-n-brand">
                {{ getInitials(item) }}
              </span>
            </div>
          </div>

          <!-- Info -->
          <div class="flex-1 min-w-0">
            <p class="text-label font-medium text-n-slate-12 truncate">
              {{
                item.item_details?.title ||
                item.title ||
                t('PIPELINE.ITEM_DETAILS.UNTITLED_ITEM')
              }}
            </p>
            <div class="flex items-center gap-1.5 mt-0.5">
              <span
                class="px-1.5 py-0.5 text-label-small font-medium rounded-full"
                :class="[
                  priorityConfig(
                    item.item_details?.priority || item.priority || 'none'
                  ).bg,
                  priorityConfig(
                    item.item_details?.priority || item.priority || 'none'
                  ).text,
                ]"
              >
                {{
                  t(
                    `PIPELINE.PRIORITY_LABELS.${(item.item_details?.priority || item.priority || 'NONE').toUpperCase()}`
                  )
                }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end gap-2">
        <Button variant="ghost" color="slate" size="sm" @click="$emit('close')">
          {{ t('PIPELINE.BULK_ACTIONS.CANCEL') }}
        </Button>
        <Button
          variant="solid"
          color="blue"
          size="sm"
          :disabled="!selectedStage || !selectedItems.length"
          @click="handleConfirm"
        >
          {{
            t('PIPELINE.BULK_ACTIONS.CONFIRM', {
              count: selectedItems.length,
            })
          }}
        </Button>
      </div>
    </div>
  </div>
</template>
